import classNames from 'classnames';
import * as React from 'react';
import type { GalleryColorVariant } from '../../GalleryColorVariant';
import type { GalleryDesktopHeightVariant } from '../../GalleryDesktopHeightVariant';
import type { GalleryMobileHeightVariant } from '../../GalleryMobileHeightVariant';
import type { BaseImage } from '~/Partials/Picture/Picture';
import { Picture } from '~/Partials/Picture/Picture';
import { PictureProfiles } from '~/Partials/Picture/PictureProfiles';
import { assertNever } from '~/Partials/Utils/typeUtils';
import styles from './GalleryItem.module.css';
import type { GalleryItemAspectRatio } from './GalleryItemAspectRatio';

export interface GalleryItemProps {
    image: BaseImage;
    ratio: GalleryItemAspectRatio;
    caption?: string;
    desktopHeightVariant?: GalleryDesktopHeightVariant;
    mobileHeightVariant?: GalleryMobileHeightVariant;
    colorVariant?: GalleryColorVariant;
}

const getProfile = (ratio: GalleryItemAspectRatio) => {
    switch (ratio) {
        case 'square':
            return PictureProfiles.galleryItemSquare;
        case 'vertical':
            return PictureProfiles.galleryItemVertical;
        case 'horizontal':
            return PictureProfiles.galleryItemHorizontal;
        default:
            return assertNever(ratio);
    }
};

export const GalleryItem: React.FC<GalleryItemProps> = props => {
    const {
        image,
        ratio,
        caption,
        desktopHeightVariant = 'tall',
        mobileHeightVariant = 'equal',
        colorVariant = 'light',
    } = props;

    const profile = getProfile(ratio);

    const getPicture = () => <Picture {...image} profile={profile} />;

    const getPictureWithCaption = () => (
        <figure>
            {getPicture()}
            <figcaption>
                <span>{caption}</span>
            </figcaption>
        </figure>
    );

    const pictureContent = caption ? getPictureWithCaption() : getPicture();

    return (
        <div
            className={classNames(
                styles.GalleryItem,
                styles[`Variant--${desktopHeightVariant}`],
                styles[`Variant--${mobileHeightVariant}`],
                styles[`Variant--${colorVariant}`],
                styles[`Variant--${ratio}`],
                caption && styles['Variant--withCaption']
            )}
        >
            {pictureContent}
        </div>
    );
};
